import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Button, Pagination, Popover, Space, Table, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeleteScaleApi from "../../api/scales/DeleteScaleApi";
import DisableScaleApi from "../../api/scales/DisableScaleApi";
import EnableScaleApi from "../../api/scales/EnableScaleApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import ScalesUnitCard from "../cards/ScalesUnitCard";
import AddScaleUnitsModal from "../modal/add/AddScaleUnitsModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditScaleNameModal from "../modal/edit/EditScaleNameModal";

function ScalesTable(props) {
  const [editModal, setEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState("");
  const [disableModal, setDisableModal] = useState(false);
  const [enableModal, setEnableModal] = useState(false);
  const [page, setPage] = useState(1);
  const [addUnitModal, setAddUnitModal] = useState(false);
  const [selectedScale, setSelectedScale] = useState();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  
  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function disableScale() {
    const apiResponse = await DisableScaleApi(selectedScale?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Scale disabled");
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't disable. Try again later."
      );
    }
    setSelectedScale();
  }

  async function enableScale() {
    const apiResponse = await EnableScaleApi(selectedScale?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Scale enabled");
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't enable. Try again later.");
    }
    setSelectedScale();
  }

  async function deleteScale() {
    const apiResponse = await DeleteScaleApi(selectedScale?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      if (props.data.scales?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedScale();
  }

  function getDefaultUnit(record) {
    let defaultUnit = "";
    record.scaleUnits.map((unit) => {
      if (unit.default === true) {
        defaultUnit = unit.symbol;
      }
    });
    return defaultUnit;
  }

  const columns = [
    {
      title: "Scale Name",
      dataIndex: "scaleName",
      key: "scaleName",
      sorter: (a, b) => {
        const aName = a.name || "";
        const bName = b.name || "";
        return aName.localeCompare(bName);
      },
      render: (text, record) => <div>{record.name}</div>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => {
        const aType = a.default ? "Default" : "Custom";
        const bType = b.default ? "Default" : "Custom";
        return aType.localeCompare(bType);
      },
      filters: [
        { text: 'Default', value: 'default' },
        { text: 'Custom', value: 'custom' },
      ],
      onFilter: (value, record) => {
        if (value === 'default') return record.default === true;
        if (value === 'custom') return record.default !== true;
        return false;
      },
      render: (text, record) => (
        <div>{record.default == true ? "Default" : "Custom"}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const aStatus = a.status || "";
        const bStatus = b.status || "";
        return aStatus.localeCompare(bStatus);
      },
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text, record) => (
        <Tag color={getColorStatus(text)}>{record.status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Default unit",
      dataIndex: "defaultUnit",
      key: "defaultUnit",
      sorter: (a, b) => {
        const aUnit = getDefaultUnit(a);
        const bUnit = getDefaultUnit(b);
        return aUnit.localeCompare(bUnit);
      },
      render: (text, record) => <div>{getDefaultUnit(record)}</div>,
    },
    Table.EXPAND_COLUMN,
    {
      title: "Units",
      dataIndex: "units",
      key: "units",
      sorter: (a, b) => {
        const aUnits = a.scaleUnits?.length || 0;
        const bUnits = b.scaleUnits?.length || 0;
        return aUnits - bUnits;
      },
      render: (text, record) => (
        <div>
          {record.name === "Number" || record.name === "Percentage"
            ? "-"
            : record.scaleUnits?.length}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "scales", "edit") &&
            (!record.default ? (
              <Tooltip title={"Edit"}>
                <div
                  onClick={() => {
                    setEditModal(true);
                    setSelectedScale(record);
                  }}
                  className="text-blue-500 hover:text-blue-600 cursor-pointer"
                >
                  <EditOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={
                  <div className="text-xs">Can't edit default scale.</div>
                }
                trigger="hover"
              >
                <div className="text-gray-400">
                  <EditOutlined />
                </div>
              </Popover>
            ))}
          {permissionAbility(userLevel, "scales", "edit") &&
            (record.name != "Number" ? (
              record.status === "Active" ? (
                <Tooltip title={"Disable"}>
                  <div
                    onClick={() => {
                      setDisableModal(true);
                      setSelectedScale(record);
                    }}
                    className="text-red-500 hover:text-red-600 cursor-pointer"
                  >
                    <StopOutlined />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip title={"Enable"}>
                  <div
                    onClick={() => {
                      setEnableModal(true);
                      setSelectedScale(record);
                    }}
                    className="text-green-500 hover:text-green-600 cursor-pointer"
                  >
                    <CheckCircleOutlined />
                  </div>
                </Tooltip>
              )
            ) : (
              <Popover
                placement="left"
                content={
                  <div className="text-xs">Can't disable this scale.</div>
                }
                trigger="hover"
              >
                <div className="text-gray-400">
                  <StopOutlined />
                </div>
              </Popover>
            ))}
          {permissionAbility(userLevel, "scales", "delete") &&
            (!record.default ? (
              record.canDelete ? (
                <Tooltip title={"Delete"}>
                  <div
                    onClick={() => {
                      setDeleteModal("delete");
                      setSelectedScale(record);
                    }}
                    className="text-red-500 hover:text-red-600 cursor-pointer"
                  >
                    <DeleteOutlined />
                  </div>
                </Tooltip>
              ) : record.canArchive ? (
                <Tooltip title={"Archive"}>
                  <div
                    onClick={() => {
                      setDeleteModal("archive");
                      setSelectedScale(record);
                    }}
                    className="text-orange-500 hover:text-orange-600 cursor-pointer"
                  >
                    <FolderAddOutlined />
                  </div>
                </Tooltip>
              ) : (
                <Popover
                  placement="left"
                  content={"This scale can't be deleted"}
                  trigger="hover"
                >
                  <div className="text-gray-500">
                    <FolderAddOutlined />
                  </div>
                </Popover>
              )
            ) : (
              <Popover
                placement="left"
                content={
                  <div className="text-xs">Can't delete default scale.</div>
                }
                trigger="hover"
              >
                <div className="text-gray-400">
                  <DeleteOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  function getColorStatus(status) {
    if (status === "Active") {
      return "green";
    } else {
      return "red";
    }
  }

  return (
    <>
      <Table
        columns={columns}
        dataSource={props.data?.scales}
        loading={loading}
        pagination={false}
        rowKey={(record) => record.id}
        rowExpandable={(record) => record.scaleUnits?.length !== 0}
        expandable={{
          expandedRowRender: (record) => (
            <>
              <p
                style={{
                  margin: 0,
                }}
              >
                <div className="grid gap-x-4 gap-y-4 grid-cols-4">
                  {record?.scaleUnits?.map((unit) => (
                    <ScalesUnitCard
                      scales={record}
                      record={unit}
                      page={page}
                      changePage={() => props.changePage(page)}
                    />
                  ))}
                </div>
                <div>
                  <Button
                    className="mt-2"
                    type="primary"
                    onClick={() => {
                      setAddUnitModal(true);
                      setSelectedScale(record);
                    }}
                  >
                    Add ScaleUnits
                  </Button>
                </div>
              </p>
            </>
          ),
          rowExpandable: (record) =>
            record.name !== "Number" && record.name !== "Percentage",
        }}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => props.changePage(num)}
          current={page}
          total={props.data?.totalCount || 0}
          pageSize={props?.data?.limit}
        />
      </div>
      <AddScaleUnitsModal
        visible={addUnitModal}
        handleCancel={() => setAddUnitModal(false)}
        scaleId={selectedScale?.id}
        handleOk={() => props.changePage(page)}
      />
      <EditScaleNameModal
        visible={editModal}
        handleCancel={() => {
          setEditModal(false);
          setSelectedScale();
        }}
        scale={selectedScale}
        handleOk={() => props.changePage(page)}
      />
      <ConfirmationModal
        visible={disableModal}
        handleCancel={() => {
          setDisableModal(false);
          setSelectedScale();
        }}
        handleOk={() => disableScale()}
        text="Are you sure want to disable this scale?"
        title="Disable scale"
      />
      <ConfirmationModal
        visible={enableModal}
        handleCancel={() => {
          setEnableModal(false);
          setSelectedScale();
        }}
        handleOk={() => enableScale()}
        text="Are you sure want to enable this scale?"
        title="Enable scale"
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal("");
          setSelectedScale();
        }}
        handleOk={() => deleteScale()}
        text={`Are you sure want to ${deleteModal} this scale?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
    </>
  );
}
export default ScalesTable;
